import type { AppLocale } from '../messages';

/**
 * Строки участка «account». Свой файл у каждого участка, а не общий `messages.ts` на 4300 строк:
 * участки правятся параллельно, и общий файл давал бы конфликт на каждой новой строке.
 * Ключ добавляется во все четыре словаря — `en` задаёт набор, остальные проверяются типом.
 *
 * <p>Обращение — на «вы» (ru), «Vi» (sr) и «vous» (fr) везде, включая подсказки: смесь «ты» и
 * «вы» внутри одного экрана читается как недоделка (аудит удобства 2026-10-04, §4).
 */
const en = {
  loginWrongCredentials: 'Wrong login or password',
  loginEmailNotVerified: 'This address is not confirmed yet. Open the link from the email we sent.',
  loginAccountDeleted: 'This account has been deleted.',
  registerClosed: 'Sign-up is closed right now',
  loginLanguageChipA11y: 'Language: {name}. Tap to change',
  loginShowPassword: 'Show password',
  loginHidePassword: 'Hide password',
  loginPasswordA11y: 'Password',
  passwordHintRule: '8+ characters',
  registerLegalLead: 'By creating an account, you accept these documents:',
  resetLinkExpired: 'This link has expired. Ask for a new one — it takes a minute.',
  resetLinkUsed: 'This link has already been used. Ask for a new one if you still need it.',
  resetLinkInvalid: 'This link does not work. Ask for a new one.',
  resetRequestNewLink: 'Send a new link',
  settingsNameRequired: 'Enter a name — it is what people see in chats.',
  settingsWrongCurrentPassword: 'Wrong current password',
  settingsUnsavedSave: 'Save',
  settingsSaveFailedStay: 'Not everything was saved — see the message under the field.',
  settingsLoginExplain: 'Used only to sign in, nobody else sees it. People find you by your #handle.',
  logoutConfirmTitle: 'Log out?',
  logoutConfirmBody: 'To get back in you will need your email or login and password. Chats stay on the server.',
  settingsNotificationsSection: 'Notifications',
  settingsNotificationsNote:
    'Do not disturb silences messages but lets calls ring. A muted chat is silent for both its messages and its calls.',
  settingsOpenSystemSettings: 'Open system notification settings',
  settingsEnableNotifications: 'Enable notifications',
  settingsNotificationsOn: 'Notifications are on in this browser.',
  settingsNotificationsBlocked:
    'Notifications are blocked in this browser. Allow them in the site settings (the icon next to the address).',
  settingsInviteShare: 'Share invite link',
  settingsInviteMessage: 'Join me on SVYAZ: {url}',
  settingsShareNoHandleInvite:
    'You have no handle yet, so this shares a general invite link. Set a handle above and people will find you directly.',
  notifyPrimerTitle: 'Don’t miss messages and calls',
  notifyPrimerBody:
    'SVYAZ uses notifications to show new messages and to ring for incoming calls. You can change this later in Settings → App.',
  notifyPrimerEnable: 'Enable notifications',
  notifyPrimerLater: 'Not now',
  helpStartHereBadge: 'Start here',
  selfStatusSavedToast: 'Status saved',
} as const satisfies Record<string, string>;

export type AccountMessageKey = keyof typeof en;

export const accountMessages: Record<AppLocale, Record<AccountMessageKey, string>> = {
  en,
  ru: {
    loginWrongCredentials: 'Неверный логин или пароль',
    loginEmailNotVerified: 'Адрес ещё не подтверждён. Откройте ссылку из письма, которое мы отправили.',
    loginAccountDeleted: 'Этот аккаунт удалён.',
    registerClosed: 'Регистрация сейчас закрыта',
    loginLanguageChipA11y: 'Язык: {name}. Нажмите, чтобы сменить',
    loginShowPassword: 'Показать пароль',
    loginHidePassword: 'Скрыть пароль',
    loginPasswordA11y: 'Пароль',
    passwordHintRule: 'Не меньше 8 символов',
    registerLegalLead: 'Создавая аккаунт, вы принимаете эти документы:',
    resetLinkExpired: 'Срок ссылки истёк. Запросите новую — это минута.',
    resetLinkUsed: 'Ссылка уже использована. Если пароль всё ещё нужно сменить, запросите новую.',
    resetLinkInvalid: 'Ссылка не работает. Запросите новую.',
    resetRequestNewLink: 'Прислать новую ссылку',
    settingsNameRequired: 'Введите имя — его видят собеседники в чатах.',
    settingsWrongCurrentPassword: 'Неверный текущий пароль',
    settingsUnsavedSave: 'Сохранить',
    settingsSaveFailedStay: 'Сохранилось не всё — смотрите сообщение под полем.',
    settingsLoginExplain: 'Нужен только для входа, другие его не видят. Найти вас можно по #нику.',
    logoutConfirmTitle: 'Выйти из аккаунта?',
    logoutConfirmBody: 'Чтобы вернуться, понадобятся почта или логин и пароль. Чаты останутся на сервере.',
    settingsNotificationsSection: 'Уведомления',
    settingsNotificationsNote:
      '«Не беспокоить» глушит сообщения, но звонки проходят. Чат с выключенным звуком молчит и о сообщениях, и о звонках.',
    settingsOpenSystemSettings: 'Открыть системные настройки уведомлений',
    settingsEnableNotifications: 'Включить уведомления',
    settingsNotificationsOn: 'Уведомления в этом браузере включены.',
    settingsNotificationsBlocked:
      'Уведомления запрещены в браузере. Разрешите их в настройках сайта (значок рядом с адресом).',
    settingsInviteShare: 'Поделиться приглашением',
    settingsInviteMessage: 'Присоединяйтесь ко мне в SVYAZ: {url}',
    settingsShareNoHandleInvite:
      'Ника пока нет, поэтому отправится общая ссылка-приглашение. Задайте ник выше — и вас будут находить напрямую.',
    notifyPrimerTitle: 'Не пропускайте сообщения и звонки',
    notifyPrimerBody:
      'Через уведомления SVYAZ показывает новые сообщения и звонит при входящем вызове. Это можно изменить позже в «Настройки → Приложение».',
    notifyPrimerEnable: 'Включить уведомления',
    notifyPrimerLater: 'Не сейчас',
    helpStartHereBadge: 'С чего начать',
    selfStatusSavedToast: 'Статус сохранён',
  },
  sr: {
    loginWrongCredentials: 'Pogrešno korisničko ime ili lozinka',
    loginEmailNotVerified: 'Adresa još nije potvrđena. Otvorite link iz poruke koju smo vam poslali.',
    loginAccountDeleted: 'Ovaj nalog je obrisan.',
    registerClosed: 'Registracija je trenutno zatvorena',
    loginLanguageChipA11y: 'Jezik: {name}. Dodirnite da promenite',
    loginShowPassword: 'Prikaži lozinku',
    loginHidePassword: 'Sakrij lozinku',
    loginPasswordA11y: 'Lozinka',
    passwordHintRule: 'Najmanje 8 znakova',
    registerLegalLead: 'Otvaranjem naloga prihvatate ove dokumente:',
    resetLinkExpired: 'Link je istekao. Zatražite novi — to traje minut.',
    resetLinkUsed: 'Link je već iskorišćen. Ako vam lozinka i dalje treba, zatražite novi.',
    resetLinkInvalid: 'Link ne radi. Zatražite novi.',
    resetRequestNewLink: 'Pošalji novi link',
    settingsNameRequired: 'Unesite ime — njega sagovornici vide u četovima.',
    settingsWrongCurrentPassword: 'Pogrešna trenutna lozinka',
    settingsUnsavedSave: 'Sačuvaj',
    settingsSaveFailedStay: 'Nije sve sačuvano — pogledajte poruku ispod polja.',
    settingsLoginExplain: 'Služi samo za prijavu i drugi ga ne vide. Pronalaze vas po #nadimku.',
    logoutConfirmTitle: 'Odjaviti se?',
    logoutConfirmBody: 'Za povratak su vam potrebni adresa ili korisničko ime i lozinka. Četovi ostaju na serveru.',
    settingsNotificationsSection: 'Obaveštenja',
    settingsNotificationsNote:
      '„Ne uznemiravaj“ utišava poruke, ali pozivi zvone. Utišan čet ćuti i za poruke i za pozive.',
    settingsOpenSystemSettings: 'Otvori sistemska podešavanja obaveštenja',
    settingsEnableNotifications: 'Uključi obaveštenja',
    settingsNotificationsOn: 'Obaveštenja su uključena u ovom pregledaču.',
    settingsNotificationsBlocked:
      'Obaveštenja su blokirana u pregledaču. Dozvolite ih u podešavanjima sajta (ikonica pored adrese).',
    settingsInviteShare: 'Podeli pozivnicu',
    settingsInviteMessage: 'Pridružite mi se u SVYAZ-u: {url}',
    settingsShareNoHandleInvite:
      'Još nemate nadimak, pa se deli opšti link-pozivnica. Postavite nadimak iznad i ljudi će vas naći direktno.',
    notifyPrimerTitle: 'Ne propustite poruke i pozive',
    notifyPrimerBody:
      'Preko obaveštenja SVYAZ prikazuje nove poruke i zvoni za dolazne pozive. To možete promeniti kasnije u Podešavanja → Aplikacija.',
    notifyPrimerEnable: 'Uključi obaveštenja',
    notifyPrimerLater: 'Ne sada',
    helpStartHereBadge: 'Odavde počnite',
    selfStatusSavedToast: 'Status je sačuvan',
  },
  fr: {
    loginWrongCredentials: 'Identifiant ou mot de passe incorrect',
    loginEmailNotVerified: 'Cette adresse n’est pas encore confirmée. Ouvrez le lien de l’e-mail envoyé.',
    loginAccountDeleted: 'Ce compte a été supprimé.',
    registerClosed: 'Les inscriptions sont fermées pour le moment',
    loginLanguageChipA11y: 'Langue : {name}. Touchez pour changer',
    loginShowPassword: 'Afficher le mot de passe',
    loginHidePassword: 'Masquer le mot de passe',
    loginPasswordA11y: 'Mot de passe',
    passwordHintRule: '8 caractères minimum',
    registerLegalLead: 'En créant un compte, vous acceptez ces documents :',
    resetLinkExpired: 'Ce lien a expiré. Demandez-en un nouveau, cela prend une minute.',
    resetLinkUsed: 'Ce lien a déjà servi. Demandez-en un nouveau si besoin.',
    resetLinkInvalid: 'Ce lien ne fonctionne pas. Demandez-en un nouveau.',
    resetRequestNewLink: 'Envoyer un nouveau lien',
    settingsNameRequired: 'Saisissez un nom — c’est ce que voient vos interlocuteurs.',
    settingsWrongCurrentPassword: 'Mot de passe actuel incorrect',
    settingsUnsavedSave: 'Enregistrer',
    settingsSaveFailedStay: 'Tout n’a pas été enregistré — voyez le message sous le champ.',
    settingsLoginExplain:
      'Sert uniquement à la connexion, personne d’autre ne le voit. On vous trouve par votre #pseudo.',
    logoutConfirmTitle: 'Se déconnecter ?',
    logoutConfirmBody:
      'Pour revenir, il vous faudra votre e-mail ou identifiant et votre mot de passe. Les discussions restent sur le serveur.',
    settingsNotificationsSection: 'Notifications et sons',
    settingsNotificationsNote:
      '« Ne pas déranger » coupe les messages mais laisse sonner les appels. Une discussion en sourdine se tait pour ses messages comme pour ses appels.',
    settingsOpenSystemSettings: 'Ouvrir les réglages système des notifications',
    settingsEnableNotifications: 'Activer les notifications',
    settingsNotificationsOn: 'Les notifications sont activées dans ce navigateur.',
    settingsNotificationsBlocked:
      'Les notifications sont bloquées dans ce navigateur. Autorisez-les dans les paramètres du site (l’icône près de l’adresse).',
    settingsInviteShare: 'Partager une invitation',
    settingsInviteMessage: 'Rejoignez-moi sur SVYAZ : {url}',
    settingsShareNoHandleInvite:
      'Vous n’avez pas encore de pseudo : un lien d’invitation général sera partagé. Choisissez un pseudo ci-dessus pour qu’on vous trouve directement.',
    notifyPrimerTitle: 'Ne manquez ni messages ni appels',
    notifyPrimerBody:
      'SVYAZ utilise les notifications pour afficher les nouveaux messages et faire sonner les appels entrants. Modifiable plus tard dans Réglages → Application.',
    notifyPrimerEnable: 'Activer les notifications',
    notifyPrimerLater: 'Plus tard',
    helpStartHereBadge: 'Pour commencer',
    selfStatusSavedToast: 'Statut enregistré',
  },
};
